import { ApiError } from '@/api/client'
import { translate } from '@/i18n'
import { useUiStore, type ToastKind } from '@/stores/ui'
import { apiErrorMessage } from '@/utils/apiErrorText'

export interface ToastApi {
  success(title: string, description?: string): void
  info(title: string, description?: string): void
  warning(title: string, description?: string): void
  error(title: string, description?: string): void
  /** Surfaces an {@link ApiError} verbatim — code, detail and trace id included. */
  apiError(fallbackTitle: string, error: unknown): void
  dismiss(id: number): void
}

export function useToast(): ToastApi {
  const ui = useUiStore()

  const push = (kind: ToastKind, title: string, description?: string): void => {
    ui.pushToast(description ? { kind, title, description } : { kind, title })
  }

  return {
    success: (title, description) => push('success', title, description),
    info: (title, description) => push('info', title, description),
    warning: (title, description) => push('warning', title, description),
    error: (title, description) => push('error', title, description),
    apiError: (fallbackTitle, error) => {
      if (error instanceof ApiError) {
        // The trace id is what support will ask for; never swallow it.
        const parts = [apiErrorMessage(error)]
        if (error.code) parts.push(translate('common.errors.code', { code: error.code }))
        if (error.traceId) parts.push(translate('common.errors.trace', { id: error.traceId }))
        push('error', error.title ?? fallbackTitle, parts.join(' · '))
        return
      }
      push('error', fallbackTitle, error instanceof Error ? error.message : String(error))
    },
    dismiss: (id) => ui.dismissToast(id),
  }
}
